/* LEGENDARY ACTIONS AND LAIR ACTIONS.
 *
 * The hardest things to track by hand, in exactly the fights where tracking
 * matters most. Both are absent from the console entirely, so a DM running a
 * dragon counts legendary actions on their fingers while also running the
 * dragon.
 *
 * This is TRACKING ONLY — a budget, a counter and a prompt. Nothing here rolls
 * anything or chooses a target: standing rule 9, and legendary actions are
 * precisely where a DM wants to be making the choices.
 *
 * The two have different clocks and that is the whole subtlety:
 *
 *   LEGENDARY  a budget of N per ROUND, spent between other creatures' turns,
 *              and refilled at the START of the owner's turn. It cannot be
 *              spent on the owner's own turn, and a creature that is
 *              incapacitated cannot spend it at all.
 *   LAIR       one action per round at initiative count 20, losing ties. It is
 *              not the creature's and is not spent from the creature's budget;
 *              a lair acts even while its owner is stunned.
 */
import type { Layout } from "./types";

export interface LegendaryState {
  /** tracker row id of the creature that has them */
  id: string;
  name: string;
  /** actions per round, as the statblock says ("can take 3 legendary actions") */
  budget: number;
  /** how many are left this round */
  left: number;
  /** the round the budget was last refilled, so a refill happens once */
  refilledRound: number;
}
export interface LairState { id: string; name: string; /** the round it last acted */ actedRound: number }

const LEG = "legendaryActions";
const LAIR = "lairActions";

export const legendaryOf = (layout: Layout): Record<string, LegendaryState> =>
  ((layout[LEG] as Record<string, Record<string, LegendaryState>> | undefined)?.[layout.activeCampaign]) || {};
export const lairOf = (layout: Layout): Record<string, LairState> =>
  ((layout[LAIR] as Record<string, Record<string, LairState>> | undefined)?.[layout.activeCampaign]) || {};

const put = <T,>(layout: Layout, key: string, id: string, value: T | null): Layout => {
  const all = { ...((layout[key] as Record<string, Record<string, T>>) || {}) };
  const mine = { ...(all[layout.activeCampaign] || {}) };
  if (value) mine[id] = value; else delete mine[id];
  all[layout.activeCampaign] = mine;
  return { ...layout, [key]: all };
};
export const withLegendary = (layout: Layout, id: string, value: LegendaryState | null) => put(layout, LEG, id, value);
export const withLair = (layout: Layout, id: string, value: LairState | null) => put(layout, LAIR, id, value);

/** Give a creature legendary actions, or change the budget. 0 removes them. */
export function setLegendary(layout: Layout, row: { id: string; name: string }, budget: number, round: number): Layout {
  if (budget <= 0) return withLegendary(layout, row.id, null);
  const had = legendaryOf(layout)[row.id];
  return withLegendary(layout, row.id, {
    id: row.id, name: row.name, budget,
    /* Raising the budget mid-fight does not hand back actions already spent. */
    left: had ? Math.min(had.left, budget) : budget,
    refilledRound: had ? had.refilledRound : round,
  });
}

/** Spend one. Returns the layout unchanged when there is nothing to spend. */
export function spendLegendary(layout: Layout, id: string, n = 1): Layout {
  const s = legendaryOf(layout)[id];
  if (!s || s.left < n) return layout;
  return withLegendary(layout, id, { ...s, left: s.left - n });
}
export function giveBackLegendary(layout: Layout, id: string, n = 1): Layout {
  const s = legendaryOf(layout)[id];
  if (!s) return layout;
  return withLegendary(layout, id, { ...s, left: Math.min(s.budget, s.left + n) });
}

/** The owner's turn has begun: their budget refills, once per round. */
export function refillOnTurn(layout: Layout, startedId: string, round: number): Layout {
  const s = legendaryOf(layout)[startedId];
  if (!s || s.refilledRound === round || s.left === s.budget) {
    return s && s.refilledRound !== round ? withLegendary(layout, startedId, { ...s, refilledRound: round }) : layout;
  }
  return withLegendary(layout, startedId, { ...s, left: s.budget, refilledRound: round });
}

/** Who may spend one right now: everybody with a budget left, except whoever is
 *  acting (you cannot take a legendary action on your own turn) and anyone the
 *  tracker says is incapacitated. */
export function canAct(layout: Layout, currentTurnId: string, incapacitated: (id: string) => boolean): LegendaryState[] {
  return Object.values(legendaryOf(layout))
    .filter((s) => s.left > 0 && s.id !== currentTurnId && !incapacitated(s.id))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Has this lair acted in this round yet? */
export const lairPending = (layout: Layout, round: number): LairState[] =>
  Object.values(lairOf(layout)).filter((l) => l.actedRound !== round).sort((a, b) => a.name.localeCompare(b.name));
export const markLairActed = (layout: Layout, id: string, round: number): Layout => {
  const l = lairOf(layout)[id];
  return l ? withLair(layout, id, { ...l, actedRound: round }) : layout;
};
export function setLair(layout: Layout, row: { id: string; name: string }, on: boolean, round: number): Layout {
  return on ? withLair(layout, row.id, { id: row.id, name: row.name, actedRound: round - 1 }) : withLair(layout, row.id, null);
}

/** Initiative 20 is where a lair acts, and it loses ties. */
export const LAIR_INITIATIVE = 20;
